<template>
  <b-container class="max-width-100" fluid>

    <slot name="header">
      <div v-if="instructions">
        <p v-for="(instruction, index) in instructions" :key="index"
           :class="['ma-2 ', {'custom-text-danger': instruction.highlighted}]">
          {{ instruction.text }}
        </p>
      </div>
    </slot>

    <b-table
      :current-page="currentPage"
      :fields="fields"
      :items="members"
      :per-page="perPage"
      bordered
      class="child-flex"
      small
      striped>

      <template v-slot:cell(full_name)="row">
        <span>{{ row.item.full_name }}</span>
      </template>

      <template v-slot:cell(email)="row">
        <custom-single-select
          v-if="!row.item.is_submitted && selectorOn"
          v-model="row.item.email"
          :editable="editable"
          :minSearchLength=5
          :options="emails"
          :required="true"
          @input="clearRow($event, row)"
        ></custom-single-select>
        <span v-else>{{ row.item.email }}</span>
        <span v-if="row.item._rowVariant" class="custom-text-danger font-weight-bold">Email duplicated</span>
      </template>

      <template v-slot:cell(main_author)="row">
        <div v-if="row.item.roles.includes('MAIN_AUTHOR')">
          <b-form-checkbox v-model="row.item.roles"
                           :disabled="row.item.roles.includes('MAIN_AUTHOR')"
                           value="MAIN_AUTHOR">
          </b-form-checkbox>
        </div>
      </template>

      <template v-slot:cell(coauthor)="row">
        <div v-if="!row.item.roles.includes('MAIN_AUTHOR') && row.item.email">
          <b-form-checkbox v-model="row.item.roles"
                           :disabled="!editable"
                           value="CO_AUTHOR"
                           @change="touch(row)">
          </b-form-checkbox>
        </div>
      </template>

      <template v-slot:cell(proposal_editor)="row">
        <div v-if="!row.item.roles.includes('MAIN_AUTHOR') && row.item.email">
          <b-form-checkbox v-model="row.item.roles"
                           :disabled="!editable"
                           value="PROPOSAL_EDITOR"
                           @change="touch(row)">
          </b-form-checkbox>
        </div>
      </template>

      <template v-slot:cell(experimentalist)="row">
        <div v-if="!row.item.roles.includes('MAIN_AUTHOR') && row.item.email">
          <b-form-checkbox v-model="row.item.roles"
                           :disabled="!editable"
                           value="EXPERIMENTALIST"
                           @change="touch(row)">
          </b-form-checkbox>
        </div>
      </template>

    </b-table>

    <b-row>
      <b-col class="mr-auto p-3"
             cols="auto">
        <b-pagination v-if="members.length > perPage"
                      v-model="currentPage"
                      :per-page="perPage"
                      :total-rows="members.length"
                      class="my-0"/>
      </b-col>
      <b-col class="p-3"
             cols="auto">
        <b-button v-if="isNewMember"
                  :disabled="disabled"
                  class="float-left action-buttons"
                  variant="info"
                  @click="submitMembers()">
          <template v-if="isSubmitting">
            <b-spinner small/>
          </template>
          Confirm roles and send invitation
        </b-button>
      </b-col>
    </b-row>

  </b-container>
</template>

<script>
  import CustomSingleSelect from '@/components/custom-single-select';
  import { includesAny } from '@/helpers';
  import _ from 'lodash';
  import instructions from '@/json/teamFormInstructions';

  export default {
    name: 'TeamForm',
    components: { CustomSingleSelect },
    props: {
      editable: {
        type: Boolean,
        default: true,
      },
      selectorOn: {
        type: Boolean,
        default: true,
      },
      proposalObjectId: {
        type: String,
        required: true,
      },
      oldCryoemTeamRolesMapping: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        fields: ['full_name', 'email', 'main_author', 'coauthor', 'proposal_editor', 'experimentalist'],
        emails: [],
        perPage: 10,
        members: [],
        membersSent: [],
        currentPage: 1,
        isNewMember: false,
        disabled: false,
        isSubmitting: false,
        instructions,
      };
    },
    computed: {
      areOldCryoemRolesInTeamMembers() {
        const oldCryoemTeamRoles = ['principle_investigator', 'main_proposer', 'experimentalist'];
        return this.members.some((member) => includesAny(member.roles, oldCryoemTeamRoles));
      },
    },
    methods: {
      checkIfEmpty(row) {
        if (row.item.roles.length === 0 && this.hasProperty(row.item, 'is_submitted')) {
          this.members.splice(row.index + (this.perPage * (this.currentPage - 1)), 1);
        }
      },
      clearRow($event, row) {
        if (!$event) {
          // eslint-disable-next-line no-param-reassign
          row.item.roles = [];
          // eslint-disable-next-line no-param-reassign,no-underscore-dangle
          delete row.item._rowVariant;
        }
      },
      getEmails() {
        this.axios.get('/users/emails')
          .then((resp) => {
            this.emails = resp.data;
          });
      },
      isTeamChanged() {
        const oldElements = this.membersSent;
        const newElements = this.members;
        return !_.isEqual(oldElements, newElements);
      },
      makeMembersCopy() {
        this.membersSent = JSON.parse(JSON.stringify(this.members));
      },
      touch(row) {
        // eslint-disable-next-line no-param-reassign
        row.item.dirty = true;
        this.isNewMember = true;
        // change event is executed before this.members object is updated ->
        // due to this, setTimeout had to be implemented
        setTimeout(() => {
          this.disabled = !this.isTeamChanged();
          this.checkIfEmpty(row);
        }, 0);
      },
      addMember() {
        this.members.push({
          // eslint-disable-next-line camelcase
          full_name: '',
          email: '',
          roles: [],
        });
      },
      loadMembers() {
        this.axios.get(`/documents/${this.proposalObjectId}/team`)
          .then((resp) => {
            this.members = resp.data;
            this.addMember();
            this.makeMembersCopy();
            this.mapOldCryoemTeamRoles();
          });
      },
      mapOldCryoemTeamRoles() {
        if (this.oldCryoemTeamRolesMapping && !this.editable && this.areOldCryoemRolesInTeamMembers) {
          this.members.forEach((member) => {
            if (member.roles.includes('principle_investigator') || member.roles.includes('main_proposer')) {
              member.roles.push('PROPOSAL_EDITOR');
            }
            if (member.roles.includes('experimentalist')) {
              member.roles.push('EXPERIMENTALIST');
            }
          });
        }
      },
      beforeSubmit() {
        const emailArray = [];
        return this.members.every((member) => {
          if (emailArray.indexOf(member.email) < 0) {
            emailArray.push(member.email);
            return true;
          }
          this.$set(member, '_rowVariant', 'danger');
          return false;
        });
      },
      filterMembers(members) {
        return members.filter((member) => member.email !== '');
      },
      submitMembers() {
        if (this.beforeSubmit()) {
          this.disabled = true;
          this.isSubmitting = true;
          this.axios.post(`/documents/${this.proposalObjectId}/team`, this.filterMembers(this.members))
            .then((resp) => {
              if (resp.status === 200) {
                this.members = resp.data;
                this.addMember();
                this.makeMembersCopy();
                this.$notify({ type: 'success', title: 'Team changed successfully' });
              } else {
                this.loadMembers();
                this.$notify({ type: 'error', title: 'Something went wrong' });
              }
            })
            .catch(() => {
              this.$notify({ type: 'error', title: 'Something went wrong' });
            })
            .finally(() => {
              this.isSubmitting = false;
            });
        } else {
          this.$notify({ type: 'error', title: 'Roles could not be saved. Fix the invalid row and confirm again.' });
        }
      },
    },
    created() {
      this.loadMembers();
      if (this.editable) {
        this.getEmails();
      }
    },
  };
</script>

<style scoped>
  .action-buttons {
    margin-left: 5px;
    margin-right: 5px;
  }

  table {
    table-layout: fixed;
    width: 100%;
  }

  /*Fix for style leak.
    When table-layout is fixed and b-container has fluid,
    max-width mustn't be given in %.*/
  .max-width-100 {
    max-width: 100vw;
  }
</style>
